import { useState, useEffect, useCallback } from 'react';
import { getQueueCount, syncQueuedScans, type SyncResult } from '@/lib/offlineQueue';

interface NetworkStatus {
  isOnline: boolean;
  pendingCount: number;
  sync: () => Promise<SyncResult | null>;
  refreshCount: () => Promise<void>;
}

export function useNetworkStatus(): NetworkStatus {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);

  const refreshCount = useCallback(async () => {
    try {
      const count = await getQueueCount();
      setPendingCount(count);
    } catch {
      // IndexedDB may not be available
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Poll queue count every 5 seconds
    refreshCount();
    const interval = setInterval(refreshCount, 5000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, [refreshCount]);

  const sync = useCallback(async () => {
    if (!navigator.onLine) return null;
    try {
      const result = await syncQueuedScans();
      await refreshCount();
      return result;
    } catch {
      return null;
    }
  }, [refreshCount]);

  return { isOnline, pendingCount, sync, refreshCount };
}
